/**
 * Reusable Risk Matrix Table Component
 * 
 * Visual representation of Risk Matrix based on Likelihood and Impact Severity
 * Can be used in:
 * - Tab 2: Modal for selecting Control Risk Ranking (interactive mode)
 * - Tab 4: Visual Table display (static/editable mode)
 * 
 * Features:
 * - Configurable likelihood and impact labels
 * - Configurable risk matrix data
 * - Interactive mode (clickable cells) or static mode
 * - Color-coded cells (High: Red, Med: Yellow, Low: Green)
 * - Tooltip showing likelihood and impact descriptions on hover
 * 
 * @example
 * // Interactive mode (for modal selection)
 * <RiskMatrixTable
 *   likelihoodLabels={['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain']}
 *   impactLabels={['Catastrophic', 'Major', 'Moderate', 'Minor', 'Insignificant']}
 *   riskMatrix={matrixData}
 *   interactive={true}
 *   onCellClick={(riskLevel, rowIndex, colIndex) => // console.log(riskLevel)}
 *   showTitle={true}
 * />
 * 
 * @example
 * // Static display mode (for Tab 4)
 * <RiskMatrixTable
 *   likelihoodLabels={likelihoodLabels}
 *   impactLabels={impactLabels}
 *   riskMatrix={matrixData}
 *   interactive={false}
 *   showTitle={true}
 * />
 */

import React from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';

// ========== TYPES ==========

export interface RiskMatrixTableProps {
  likelihoodLabels: string[];
  likelihoodDescriptions?: string[];
  impactLabels: string[];
  impactDescriptions?: string[];
  riskMatrix: ('High' | 'Med' | 'Low')[][];
  interactive?: boolean;
  onCellClick?: (riskLevel: 'High' | 'Med' | 'Low', rowIndex: number, colIndex: number) => void;
  selectedRow?: number;
  selectedCol?: number;
  showTitle?: boolean;
}

// ========== STYLED COMPONENTS ==========

const MatrixContainer = styled.div`
  width: 100%;
`;

const MatrixHeader = styled.div`
  text-align: center;
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #6B7280;
  padding: 12px 0;
  margin-bottom: 8px;
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: white;
`;

const HeaderRow = styled.tr`
  background: #F3F4F6;
`;

const CornerCell = styled.th`
  font-family: Inter;
  font-weight: 600;
  font-size: 13px;
  color: #6B7280;
  padding: 12px 16px;
  text-align: left;
  background: #F3F4F6;
  border-right: 1px solid #E5E7EB;
  border-bottom: 1px solid #E5E7EB;
  white-space: nowrap;
  vertical-align: middle;
  
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ArrowIcon = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  
  &::after {
    content: '↓';
    font-size: 12px;
    color: #6B7280;
  }
`;

const ColumnHeaderCell = styled.th`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #6B7280;
  padding: 12px 8px;
  text-align: center;
  background: #F3F4F6;
  border-bottom: 1px solid #E5E7EB;
  border-left: 1px solid #E5E7EB;
  border-right: 1px solid #E5E7EB;
  white-space: nowrap;
`;

const RowHeaderCell = styled.td`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #6B7280;
  padding: 12px 16px;
  text-align: left;
  background: #F9FAFB;
  border-right: 1px solid #E5E7EB;
  white-space: nowrap;
`;

const MatrixCell = styled.td<{ 
  $riskLevel: 'High' | 'Med' | 'Low';
  $interactive?: boolean;
  $selected?: boolean;
}>`
  padding: 16px 8px;
  text-align: center;
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  border: 1px solid #E5E7EB;
  cursor: ${props => props.$interactive ? 'pointer' : 'default'};
  transition: all 0.2s;
  position: relative;
  
  ${props => {
    switch (props.$riskLevel) {
      case 'High':
        return `
          background: #FEE2E2;
          color: #991B1B;
        `;
      case 'Med':
        return `
          background: #FEF3C7;
          color: #92400E;
        `;
      case 'Low':
        return `
          background: #D1FAE5;
          color: #065F46;
        `;
    }
  }}
  
  ${props => props.$selected && `
    box-shadow: inset 0 0 0 3px #2563EB;
    font-weight: 800;
    transform: scale(1.04);
    z-index: 1;
  `}
  
  ${props => props.$interactive && `
    &:hover {
      opacity: 0.8;
      transform: scale(1.02);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  `}
`;

const DataRow = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid #E5E7EB;
  }
`;

const TooltipContent = styled.div`
  font-family: Inter;
  font-size: 13px;
  line-height: 1.5;
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const TooltipDescription = styled.div`
  font-weight: 400;
  color: #6B7280;
`;

const TooltipDivider = styled.div`
  height: 12px;
`;

// ========== HELPER FUNCTIONS ==========

// Format risk level for display (Med -> Medium)
const formatRiskLevel = (riskLevel: 'High' | 'Med' | 'Low'): string => {
  return riskLevel === 'Med' ? 'Medium' : riskLevel;
};

// ========== COMPONENT ==========

const RiskMatrixTable: React.FC<RiskMatrixTableProps> = ({
  likelihoodLabels,
  likelihoodDescriptions = [],
  impactLabels,
  impactDescriptions = [],
  riskMatrix,
  interactive = false,
  onCellClick,
  selectedRow,
  selectedCol,
  showTitle = true,
}) => {
  const handleCellClick = (riskLevel: 'High' | 'Med' | 'Low', rowIndex: number, colIndex: number) => {
    if (interactive && onCellClick) {
      onCellClick(riskLevel, rowIndex, colIndex);
    }
  };

  // Render tooltip content for a cell
  const renderTooltipContent = (rowIndex: number, colIndex: number) => {
    const likelihoodDesc = likelihoodDescriptions[colIndex];
    const impactDesc = impactDescriptions[rowIndex];

    if (!likelihoodDesc && !impactDesc) return null;

    return (
      <TooltipContent>
        {likelihoodDesc && (
          <>
            <TooltipLabel>Likelihood Degree:</TooltipLabel>
            <TooltipDescription>{likelihoodDesc}</TooltipDescription>
          </>
        )}
        {likelihoodDesc && impactDesc && <TooltipDivider />}
        {impactDesc && (
          <>
            <TooltipLabel>Risk Impact Severity:</TooltipLabel>
            <TooltipDescription>{impactDesc}</TooltipDescription>
          </>
        )}
      </TooltipContent>
    );
  };

  return (
    <MatrixContainer>
      {showTitle && <MatrixHeader>Likelihood</MatrixHeader>}
      
      <MatrixTable>
        <thead>
          <HeaderRow>
            <CornerCell as="th">
              Impact Severity
              <ArrowIcon />
            </CornerCell>
            {likelihoodLabels.map((label, index) => (
              <ColumnHeaderCell key={index}>{label}</ColumnHeaderCell>
            ))}
          </HeaderRow>
        </thead>
        <tbody>
          {impactLabels.map((impactLabel, rowIndex) => (
            <DataRow key={rowIndex}>
              <RowHeaderCell>{impactLabel}</RowHeaderCell>
              {riskMatrix[rowIndex]?.map((riskLevel, colIndex) => {
                const tooltipContent = renderTooltipContent(rowIndex, colIndex);
                const isSelected = selectedRow === rowIndex && selectedCol === colIndex;
                const cell = (
                  <MatrixCell
                    key={colIndex}
                    $riskLevel={riskLevel}
                    $interactive={interactive}
                    $selected={isSelected}
                    onClick={() => handleCellClick(riskLevel, rowIndex, colIndex)}
                  >
                    {formatRiskLevel(riskLevel)}
                  </MatrixCell>
                );

                if (tooltipContent) {
                  return (
                    <Tooltip
                      key={colIndex}
                      title={tooltipContent}
                      placement="top"
                      overlayStyle={{ maxWidth: '350px' }}
                      overlayInnerStyle={{
                        backgroundColor: '#FFFFFF',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                      }}
                      color="#FFFFFF"
                    >
                      {cell}
                    </Tooltip>
                  );
                }

                return cell;
              })}
            </DataRow>
          ))}
        </tbody>
      </MatrixTable>
    </MatrixContainer>
  );
};

export default RiskMatrixTable;

